import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import "../../../page/dashboard/sustainability/SustainabilityDashboard.scss";
import {
  CustomerDataEntry,
  SiteDataEntry,
} from "../../../service/utils/interfaces";
import { DownloadProgress } from '../../../utils/fileDownloadValidator';
import FilterRow from "../../../component/filter-row/FilterRow";
import DropdownSelect from "../../../component/dropdown-select/DropdownSelect";
import SupportForm from "../../../component/support-form/SupportForm";
import TabsContainer from "../../../component/tabs-container/TabsContainer";
import Container from "../../../component/container/Container";
import SortableTable, { SortSettings } from "../../../component/sortable-table/SortableTable";
import CollapsibleAuditActionsTable, {
  CollapsibleAuditActionsRow,
} from "../../../component/collapsible-audit-actions-table/CollapsibleAuditActionsTable";
import CollapsibleProgressiveActionsTable, {
  CollapsibleProgressiveActionsRow,
} from "../../../component/collapsible-progressive-actions-table/CollapsibleProgressiveActionsTable";
import { resourceLinks } from "../../../service/utils/constants";

type SustainabilityBannerPlacement =
  | "sustainabilityTeamOfferings"
  | "sustainabilityCalendar"
  | "volunteringEvents"
  | "relatedBlogpost";

type SustainabilityBannerItem = {
  id: string;
  placement: SustainabilityBannerPlacement;
  title: string;
  link: string;
  imageUrl: string;
  autoScroll: boolean;
  displayTitle: boolean;
  readMoreButton: boolean;
  createdAt: string;
};

type SustainabilityAchievementItem = {
  id: string;
  customerNumber: string;
  siteNumber: string;
  occupier: string;
  awardName: string;
  imageUrl: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
};

interface SustainabilityDashboardUIProps {
  isLoading: boolean;
  customerList: CustomerDataEntry[];
  selectedCustomer?: CustomerDataEntry;
  sitesPerCustomer: SiteDataEntry[];
  reportData: any;
  auditOptions: {
    label: string;
    value: string;
    customerSite?: string;
    occupier?: string;
    dateCompleted?: any;
    handlebarUri?: string;
    carboneTemplateId?: string;
    isDisabled?: boolean;
  }[];
  selectedAudit: {
    label: string;
    value: string;
    customerSite?: string;
    occupier?: string;
    dateCompleted?: any;
    handlebarUri?: string;
    carboneTemplateId?: string;
    isDisabled?: boolean;
  } | null;
  isAuditListLoading: boolean;
  sustainabilityBanners: SustainabilityBannerItem[];
  sustainabilityAchievements: SustainabilityAchievementItem[];
  isSustainabilityAchievementsLoading: boolean;
  progressiveActionGroups: {
    category: string;
    sections: {
      section: string;
      actions: {
        notesId: string;
        actionSorting: string;
        question: string;
        response: string;
        actionName: string;
        owner: string;
      }[];
    }[];
  }[];
  ticketActionStatusByName: Record<string, boolean>;
  ticketActionCompletedByByName: Record<string, string>;
  isTicketActionsLoading: boolean;
  handleToggleTicketAction: (
    category: string,
    section: string,
    question: string,
    response: string,
    notesId: string,
    actionSorting: string,
    actionName: string,
    owner: string,
    checked: boolean
  ) => void;
  leaderboardEntries: {
    auditId: string;
    occupier: string;
    dateCompleted: string;
    auditScorePercent: number;
    completed: number;
    total: number;
    percentDone: number;
  }[];
  isLeaderboardLoading: boolean;
  auditActionsTableHeader: string[];
  auditActionsTableRows: any[][];
  auditActionsSortSettings?: SortSettings;
  auditActionsSearchParam: string;
  handleAuditActionsSortChanged: (col: number) => void;
  handleAuditActionsFilterChanged: (val: string) => void;
  resetAuditActionsTable: () => void;
  isAuditNotesLoading: boolean;
  legacyTableHeader: any[];
  legacyTableRows: any[][];
  legacySortSettings?: SortSettings;
  legacySearchParam: string;
  handleLegacySortChanged: (col: number) => void;
  handleLegacyFilterChanged: (val: string) => void;
  resetLegacyTable: () => void;
  username: string;
  email: string;
  exportInProgress: boolean;
  downloadProgress: DownloadProgress | null;
  setSelectedCustomer: (customer: CustomerDataEntry) => void;
  setSelectedSites: (sites: SiteDataEntry[]) => void;
  setSelectedAudit: (audit: {
    label: string;
    value: string;
    customerSite?: string;
    occupier?: string;
    dateCompleted?: any;
    handlebarUri?: string;
    carboneTemplateId?: string;
    isDisabled?: boolean;
  } | null) => void;
  handleGetReport: () => void;
  handleExportPDF: () => void;
  darkMode: boolean;
  width?: number;
}

const SustainabilityDashboardUI = memo((props: SustainabilityDashboardUIProps) => {
  const [currentTab, setCurrentTab] = useState<number>(0);

  const {
    progressiveActionGroups,
    ticketActionStatusByName,
    ticketActionCompletedByByName,
    handleToggleTicketAction,
  } = props;

  const bannerPlacements: SustainabilityBannerPlacement[] = useMemo(
    () => [
      "relatedBlogpost",
      "volunteringEvents",
      "sustainabilityCalendar",
      "sustainabilityTeamOfferings",
    ],
    []
  );

  const bannersByPlacement = useMemo(() => {
    const init: Record<SustainabilityBannerPlacement, SustainabilityBannerItem[]> = {
      relatedBlogpost: [],
      volunteringEvents: [],
      sustainabilityCalendar: [],
      sustainabilityTeamOfferings: [],
    };

    const rows = Array.isArray(props.sustainabilityBanners) ? props.sustainabilityBanners : [];
    rows.forEach((b) => {
      if (!b?.placement) return;
      init[b.placement] = [...(init[b.placement] ?? []), b];
    });
    return init;
  }, [props.sustainabilityBanners]);

  const [bannerIndexByPlacement, setBannerIndexByPlacement] = useState<
    Record<SustainabilityBannerPlacement, number>
  >({
    relatedBlogpost: 0,
    volunteringEvents: 0,
    sustainabilityCalendar: 0,
    sustainabilityTeamOfferings: 0,
  });

  // Ensure index stays in range when banner counts change.
  useEffect(() => {
    setBannerIndexByPlacement((current) => {
      const next = { ...current };
      bannerPlacements.forEach((p) => {
        const count = bannersByPlacement[p]?.length ?? 0;
        if (count <= 0) {
          next[p] = 0;
          return;
        }
        const idx = next[p] ?? 0;
        next[p] = idx % count;
      });
      return next;
    });
  }, [bannerPlacements, bannersByPlacement]);

  // Auto-scroll carousels every 5 seconds when enabled.
  useEffect(() => {
    const timers: Array<number | null> = [];

    bannerPlacements.forEach((placement) => {
      const items = bannersByPlacement[placement] ?? [];
      const autoScroll = Boolean(items?.[0]?.autoScroll);
      if (!autoScroll || items.length <= 1) {
        timers.push(null);
        return;
      }

      const timer = window.setInterval(() => {
        setBannerIndexByPlacement((current) => {
          const currentIndex = current[placement] ?? 0;
          const nextIndex = (currentIndex + 1) % items.length;
          return { ...current, [placement]: nextIndex };
        });
      }, 5000);

      timers.push(timer);
    });

    return () => {
      timers.forEach((t) => {
        if (t !== null) window.clearInterval(t);
      });
    };
  }, [bannerPlacements, bannersByPlacement]);

  const getTicketActionKey = useCallback(
    (
      category: string,
      section: string,
      question: string,
      response: string,
      notesId?: string,
      actionSorting?: string
    ) => {
      return [category, section, question, response, notesId, actionSorting]
        .map((part) => String(part ?? "").trim())
        .join("||");
    },
    []
  );

  const getLegacyTicketActionKey = useCallback((category: string, actionName: string) => {
    return `${String(category ?? "").trim()}||${String(actionName ?? "").trim()}`;
  }, []);

  const progressiveActionRows = useMemo<CollapsibleProgressiveActionsRow[]>(() => {
    const rows: CollapsibleProgressiveActionsRow[] = [];

    const groups = Array.isArray(progressiveActionGroups)
      ? progressiveActionGroups
      : [];

    groups.forEach((group) => {
      const category = String(group?.category ?? "").trim();
      const sections = Array.isArray(group?.sections) ? group.sections : [];

      sections.forEach((sec) => {
        const section = String(sec?.section ?? "").trim() || "General";
        const actions = Array.isArray(sec?.actions) ? sec.actions : [];

        actions.forEach((actionItem) => {
          const actionName = String(actionItem?.actionName ?? "").trim();
          if (!actionName) return;

          const notesId = String(actionItem?.notesId ?? "").trim();
          const actionSorting = String(actionItem?.actionSorting ?? "").trim();
          const question = String(actionItem?.question ?? "").trim();
          const response = String(actionItem?.response ?? "").trim();
          const owner = String(actionItem?.owner ?? "").trim();
          const key = getTicketActionKey(
            category,
            section,
            question,
            response,
            notesId,
            actionSorting
          );
          const legacyKey = getLegacyTicketActionKey(category, actionName);
          const checked = Boolean(
            ticketActionStatusByName[key] ?? ticketActionStatusByName[legacyKey]
          );
          const completedBy =
            ticketActionCompletedByByName[key] ??
            ticketActionCompletedByByName[legacyKey] ??
            "";

          rows.push({
            category,
            section,
            actionName,
            owner,
            completedBy: checked ? String(completedBy ?? "").trim() : "",
            checked,
            onToggle: (nextChecked) =>
              handleToggleTicketAction(
                category,
                section,
                question,
                response,
                notesId,
                actionSorting,
                actionName,
                owner,
                nextChecked
              ),
          });
        });
      });
    });

    return rows;
  }, [
    progressiveActionGroups,
    ticketActionStatusByName,
    ticketActionCompletedByByName,
    handleToggleTicketAction,
    getTicketActionKey,
    getLegacyTicketActionKey,
  ]);

  const completedTrophyUrl = process.env.REACT_APP_SD_COMPLETED_TROPHY;
  const notCompletedTrophyUrl = process.env.REACT_APP_SD_NOTCOMPLETED_TROPHY;

  const sortedLeaderboardEntries = useMemo(() => {
    return [...(Array.isArray(props.leaderboardEntries) ? props.leaderboardEntries : [])].sort((a, b) => {
      const scoreA = Number(a?.auditScorePercent);
      const scoreB = Number(b?.auditScorePercent);

      const normalizedA = Number.isFinite(scoreA) ? scoreA : Number.POSITIVE_INFINITY;
      const normalizedB = Number.isFinite(scoreB) ? scoreB : Number.POSITIVE_INFINITY;

      return normalizedA - normalizedB;
    });
  }, [props.leaderboardEntries]);

  const completedActionAchievements = useMemo(() => {
    return (Array.isArray(props.leaderboardEntries) ? props.leaderboardEntries : [])
      .filter((entry) => {
        const percentDone = Number(entry?.percentDone ?? 0);
        return Number.isFinite(percentDone) && percentDone >= 100;
      })
      .map((entry, idx) => ({
        id: `completed-${entry.auditId || idx}`,
        occupier: String(entry?.occupier ?? "").trim(),
        awardName: entry?.dateCompleted
          ? `Progressive Actions\n(Audit: ${String(entry.dateCompleted).trim()})`
          : "Progressive Actions",
        imageUrl: completedTrophyUrl || notCompletedTrophyUrl || "",
      }));
  }, [props.leaderboardEntries, completedTrophyUrl, notCompletedTrophyUrl]);

  const combinedAchievements = useMemo(() => {
    const strapiAchievements = (Array.isArray(props.sustainabilityAchievements)
      ? props.sustainabilityAchievements
      : []
    ).map((achievement, idx) => ({
      id: `strapi-${achievement.id || idx}`,
      occupier: String(achievement?.occupier ?? "").trim(),
      awardName: String(achievement?.awardName ?? "").trim(),
      imageUrl: String(achievement?.imageUrl ?? "").trim(),
    }));

    return [...strapiAchievements, ...completedActionAchievements];
  }, [props.sustainabilityAchievements, completedActionAchievements]);

  const formatTrophyOccupierLabel = useCallback((value: unknown): string => {
    const text = String(value ?? "").trim();
    if (!text) return "";

    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    for (let i = 0; i < words.length; i += 3) {
      lines.push(words.slice(i, i + 3).join(" "));
    }
    return lines.join("\n");
  }, []);

  const renderBannerWidget = useCallback(
    (
      placement: SustainabilityBannerPlacement,
      title: string,
      emptyText: string
    ) => {
      const items = bannersByPlacement[placement] ?? [];
      const idxRaw = bannerIndexByPlacement[placement] ?? 0;
      const idx = items.length > 0 ? idxRaw % items.length : 0;
      const current = items[idx];

      return (
        <div className={"widget banner-widget" + (props.darkMode ? " dark" : "")}
        >
          <div className="widget-title">{title}</div>
          <div className="widget-content">
            {!current ? (
              <div className="empty-state">{emptyText}</div>
            ) : (
              <>
                <a
                  href={current.link || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="banner-link"
                >
                  <img
                    src={current.imageUrl}
                    alt={current.title || title}
                  />
                </a>

                {Boolean(current.displayTitle) || Boolean(current.readMoreButton) ? (
                  <div className="banner-meta">
                    <div className="banner-meta-left">
                      {Boolean(current.displayTitle) && String(current.title || "").trim() !== "" ? (
                        <div className="banner-item-title">{current.title}</div>
                      ) : null}
                    </div>
                    <div className="banner-meta-right">
                      {Boolean(current.readMoreButton) ? (
                        <a
                          href={current.link || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={
                            "banner-read-more-button" + (props.darkMode ? " dark" : "")
                          }
                        >
                          Read more
                        </a>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {items.length > 1 ? (
                  <div className="banner-dots" aria-label={`${title} navigation`}>
                    {items.map((_, dotIdx) => (
                      <button
                        key={`${placement}-dot-${dotIdx}`}
                        type="button"
                        className={"banner-dot" + (dotIdx === idx ? " active" : "")}
                        aria-label={`Go to slide ${dotIdx + 1}`}
                        onClick={() =>
                          setBannerIndexByPlacement((currentState) => ({
                            ...currentState,
                            [placement]: dotIdx,
                          }))
                        }
                      />
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      );
    },
    [
      bannersByPlacement,
      bannerIndexByPlacement,
      props.darkMode,
    ]
  );

  const handleCustomerChange = useCallback(
    (val: any) => {
      // Keep the same top filters for both tabs, but ensure the legacy table
      // is driven only by the top Customer selection.
      props.resetLegacyTable();
      props.resetAuditActionsTable();
      props.setSelectedCustomer(val);
    },
    [props]
  );

  if (props.isLoading) {
    return (
      <div className={"sustainability-dashboard" + (props.darkMode ? " dark" : "")}>
        <div>Loading...</div>
      </div>
    );
  }

  const dashboardPanel = (
    <>
      {/* Widgets Grid */}
      <div className="widgets-grid">
        {/* Widget 1: Audit Outcomes */}
        <div className={"widget widget--full-width" + (props.darkMode ? " dark" : "")}>
          <div className="widget-title">{process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_1}</div>
          <div className="widget-content audit-actions-content">
            {!props.selectedAudit ? (
              <div className="empty-state">Select an audit to see audit actions</div>
            ) : props.isAuditNotesLoading ? (
              <div className="empty-state">Loading audit actions…</div>
            ) : props.auditActionsTableRows && props.auditActionsTableRows.length > 0 ? (
              <CollapsibleAuditActionsTable
                resetToken={String(props.selectedAudit?.value ?? "")}
                rows={(props.auditActionsTableRows as any[][])
                  .map((r): CollapsibleAuditActionsRow => ({
                    category: String(r?.[0] ?? ""),
                    section: String(r?.[1] ?? ""),
                    notes: String(r?.[2] ?? ""),
                    actions: String(r?.[3] ?? ""),
                  }))
                  .filter(
                    (r) =>
                      r.category.trim() !== "" ||
                      r.section.trim() !== "" ||
                      r.notes.trim() !== "" ||
                      r.actions.trim() !== ""
                  )}
              />
            ) : (
              <div className="empty-state">No audit actions available</div>
            )}
          </div>
        </div>

        {/* Widget 2: Progressive Actions */}
        <div className={"widget progressive-actions-widget" + (props.darkMode ? " dark" : "")}>
          <div className="widget-title">{process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_2}</div>
          <div className="widget-content">
            {!props.selectedAudit ? (
              <div className="empty-state">Select an audit to see actions</div>
            ) : props.isAuditNotesLoading || props.isTicketActionsLoading ? (
              <div className="empty-state">Loading actions…</div>
            ) : progressiveActionRows && progressiveActionRows.length > 0 ? (
              <CollapsibleProgressiveActionsTable
                resetToken={String(props.selectedAudit?.value ?? "")}
                rows={progressiveActionRows}
                darkMode={props.darkMode}
              />
            ) : (
              <div className="empty-state">No actions available</div>
            )}
          </div>
        </div>

        {/* Widget 3: Leaderboard */}
        <div className={"widget leaderboard-widget" + (props.darkMode ? " dark" : "")}>
          <div className="widget-title">{process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_3}</div>
          <div className="widget-content">
            {props.isLeaderboardLoading ? (
              <div className="empty-state">Loading leaderboard…</div>
            ) : !props.selectedCustomer ? (
              <div className="empty-state">Select a customer to see leaderboard</div>
            ) : sortedLeaderboardEntries.length === 0 ? (
              <div className="empty-state">No actions data available</div>
            ) : (
              <div className="leaderboard-table">
                <div className="leaderboard-table-header">
                  <div>Occupier</div>
                  <div>Audit Score</div>
                  <div className="completed-actions-header">Completed Actions</div>
                </div>
                <div className="leaderboard-table-body">
                  {sortedLeaderboardEntries.map((entry, idx) => (
                    <div key={entry.auditId || `${entry.occupier}-${idx}`} className="leaderboard-table-row">
                      <div className="leaderboard-table-cell occupier">{entry.occupier}</div>
                      <div className="leaderboard-table-cell audit-score">
                        {Number.isFinite(Number(entry.auditScorePercent)) ? `${Math.round(Number(entry.auditScorePercent))}%` : ""}
                      </div>
                      <div className="leaderboard-table-cell actions-count">
                        {entry.completed}/{entry.total}
                      </div>
                      <div className="leaderboard-table-cell actions-score">
                        {entry.percentDone}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Widget 4: Achievements */}
        <div className={"widget achievements-widget" + (props.darkMode ? " dark" : "")}>
          <div className="widget-title">{process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_4}</div>
          <div className="widget-content">
            {!props.selectedCustomer ? (
              <div className="empty-state">Select a customer to see achievements</div>
            ) : props.isSustainabilityAchievementsLoading && combinedAchievements.length === 0 ? (
              <div className="empty-state">Loading achievements…</div>
            ) : combinedAchievements.length === 0 ? (
              <div className="empty-state">No achievements yet</div>
            ) : (
              <div className="trophies-horizontal-scroll">
                <div className="trophies">
                  {combinedAchievements.map((item, idx) => {
                    const src = String(item.imageUrl || "").trim() || completedTrophyUrl || notCompletedTrophyUrl;
                    const occupierLabel = formatTrophyOccupierLabel(item.occupier || "");

                    return (
                      <div key={item.id || `achievement-${idx}`} className="trophy">
                        <div className="trophy-image">
                          <img src={src} alt="Trophy" />
                        </div>
                        <div className="trophy-label">{occupierLabel || "(unknown occupier)"}</div>
                        {item.awardName ? <div className="trophy-award-name">{item.awardName}</div> : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Widget 5: Related Blogpost */}
        {renderBannerWidget(
          "relatedBlogpost",
          `${process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_5}`,
          "No blogpost available"
        )}

        {/* Widget 6: Volunteering Opportunities - Events */}
        {renderBannerWidget(
          "volunteringEvents",
          `${process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_6}`,
          "No events available"
        )}

        {/* Widget 7: Resource Links */}
        <div className={"widget" + (props.darkMode ? " dark" : "")}>
          <div className="widget-title">{process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_7}</div>
          <div className="widget-content">
            <div className="links-list">
              {resourceLinks.map((link, idx) => (
                <div key={idx} className="link-item">
                  <div className="link-icon">
                    <FontAwesomeIcon icon={link.icon} />
                  </div>
                  <div className="link-content">
                    <div className="link-title" onClick={() => window.open(link.url, "_blank")}>
                      {link.title}
                    </div>
                    <div className="link-description">{link.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Widget 8: Sustainability Calendar */}
        {renderBannerWidget(
          "sustainabilityCalendar",
          `${process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_8}`,
          "No calendar available"
        )}

        {/* Widget 9: Sustainability Team Offerings */}
        {renderBannerWidget(
          "sustainabilityTeamOfferings",
          `${process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_9}`,
          "No offerings available"
        )}

        {/* Widget 10: Book Now */}
        <div className={"widget" + (props.darkMode ? " dark" : "")}>
          <div className="widget-title">{process.env.REACT_APP_SUSTAINABILITY_WIDGET_NAME_10}</div>
          <div className="widget-content">
            <SupportForm
              username={props.username}
              email={props.email}
              darkMode={props.darkMode}
              recipient={process.env.REACT_APP_SUSTAINABILITY_WIDGET_EMAIL_TO_10}
            />
          </div>
        </div>
      </div>
    </>
  );

  const legacyPanel = (
    <div className="legacy-audits">
      <Container darkTheme={props.darkMode}>
        <div className="column">
          <SortableTable
            id="audit-list"
            width={props.width || window.innerWidth}
            header={props.legacyTableHeader}
            data={props.legacyTableRows}
            standaloneTable
            isAuditTable
            currentSort={props.legacySortSettings}
            onSortChanged={props.handleLegacySortChanged}
            currentFilterKey={props.legacySearchParam}
            onFilterChanged={props.handleLegacyFilterChanged}
            rowsPerPage={25}
            darkMode={props.darkMode}
            sourceDownload="local"
          />
        </div>
      </Container>
    </div>
  );

  return (
    <div className={"sustainability-dashboard" + (props.darkMode ? " dark" : "")}>
      <h1 className="page-title">Sustainability Dashboard</h1>

      {/* Top Filters (shared across both tabs) */}
      <div className="filter-row row">
        <FilterRow
          width={props.width || window.innerWidth}
          customerData={props.customerList}
          isTippingUser={false}
          onChangeCustomerData={handleCustomerChange}
          siteData={props.sitesPerCustomer}
          onChangeSiteData={(val) => {
            props.resetLegacyTable();
            props.resetAuditActionsTable();
            props.setSelectedSites([...val]);
          }}
          wasteTypeData={undefined} // Remove waste types filter completely
          onChangeWasteTypeData={undefined}
          displayDateRange={false}
          startDate={null}
          endDate={null}
          onChangeTimeRange={() => {}}
          darkMode={props.darkMode}
        />
        <DropdownSelect
          id="sc_audit"
          width={props.width || window.innerWidth}
          title="audit"
          all={
            props.isAuditListLoading
              ? "Loading audits..."
              : props.auditOptions.length > 0
              ? "Select Audit"
              : "No audits found"
          }
          data={
            props.auditOptions.length > 0
              ? props.auditOptions
              : [{ label: "No audits found", value: "no_audits", isDisabled: true }]
          }
          preselected={props.selectedAudit ?? undefined}
          onChange={(val) => props.setSelectedAudit(val)}
          isDisabled={props.isAuditListLoading || props.auditOptions.length === 0}
          darkMode={props.darkMode}
        />
        <button
          className="download-report-button"
          title="Download PDF Report"
          onClick={props.handleExportPDF}
          disabled={!props.selectedCustomer || !props.selectedAudit || props.exportInProgress}
          style={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity:
              !props.selectedCustomer || !props.selectedAudit || props.exportInProgress ? 0.5 : 1,
            cursor:
              !props.selectedCustomer || !props.selectedAudit || props.exportInProgress
                ? "not-allowed"
                : "pointer",
          }}
        >
          <FontAwesomeIcon
            icon={props.exportInProgress ? faSpinner : faFilePdf}
            style={{ fontSize: 22 }}
            spin={props.exportInProgress}
          />
        </button>
      </div>

      {/* Progress indicator */}
      {props.downloadProgress && (
        <div
          className="download-progress"
          style={{
            padding: "10px",
            margin: "10px 0",
            backgroundColor: props.darkMode ? "#2a2a2a" : "#f0f0f0",
            borderRadius: "4px",
            textAlign: "center",
          }}
        >
          <div>Downloading: {props.downloadProgress.percentage}%</div>
          <div style={{ fontSize: "12px", color: props.darkMode ? "#aaa" : "#666" }}>
            {(props.downloadProgress.loaded / 1024 / 1024).toFixed(2)} MB /
            {props.downloadProgress.total
              ? (props.downloadProgress.total / 1024 / 1024).toFixed(2) + " MB"
              : "Unknown"}
          </div>
        </div>
      )}

      <TabsContainer
        darkTheme={props.darkMode}
        tabList={["Dashboard", "Legacy"]}
        contentList={[dashboardPanel, legacyPanel]}
        currentTab={currentTab}
        onSwitchTab={setCurrentTab}
      />
    </div>
  );
});

export default SustainabilityDashboardUI;
